import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import type {
  InstallAppInput,
  AppCommunicationChannel,
  AppFieldConfig,
} from "skedyul-core/features/apps";
import type { ComputeLayer } from "skedyul-core/features/deployments/resources/types";
import { trpc } from "@utils/api";
import { type inferRouterOutputs, type inferRouterInputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import type {
  ModelDefinition,
  PageDefinition,
  NavigationConfig,
} from "skedyul-node";

type RouterOutput = inferRouterOutputs<AppRouter>;
type RouterInput = inferRouterInputs<AppRouter>;

type UpdateAppInput = RouterInput["apps"]["updateApp"];

export type ListApp = RouterOutput["apps"]["getApps"][number];
export type AppInstallation = RouterOutput["apps"]["getInstallation"];
export type GetApp = NonNullable<RouterOutput["apps"]["getApp"]>;

// Types for app versions
type EnvSchemaDefinition = {
  label: string;
  scope?: "install" | "provision";
  required?: boolean;
  visibility?: "visible" | "encrypted";
  default?: string;
  description?: string;
  placeholder?: string;
};

export type StoredEnvSchema = {
  global: Record<string, EnvSchemaDefinition>;
  install: Record<string, EnvSchemaDefinition>;
};

/**
 * Model definition from provision config.
 * Re-exported from skedyul-node for backward compatibility.
 */
export type ProvisionModelDef = ModelDefinition;

/**
 * Page definition from provision config.
 * Re-exported from skedyul-node for backward compatibility.
 */
export type ProvisionPageDef = PageDefinition;

export type AppVersionWithExecutable = {
  id: string;
  name: string;
  handle: string;
  isProd: boolean;
  isLocalDev?: boolean;
  executableId: string | null;
  executable: {
    id: string;
    status: string;
    invokeEndpoint: string | null;
    deploymentId: string | null;
    config?: {
      provision?: {
        env?: StoredEnvSchema["global"];
        models?: ModelDefinition[];
        pages?: PageDefinition[];
        navigation?: NavigationConfig;
      };
    } | null;
    envSchema?: StoredEnvSchema | null;
    deployment: {
      id: string;
      status: string;
      imageTagId: string | null;
      createdAt: string;
    } | null;
  } | null;
};

export type VersionEnvVariable = {
  id: string;
  key: string;
  lastFour: string | null;
  visibility: "ENCRYPTED" | "VISIBLE";
  editable: boolean;
  value: string | null;
};

export interface appState {
  apps: {
    isLoading: boolean;
    data: ListApp[];
  };
  currentApp: {
    isLoading: boolean;
    data: GetApp | null;
  };
  // Deprecated local cache. Use appInstall.data.appCommunicationChannels when needed
  installing: boolean;
  appInstall: {
    isLoading: boolean;
    data: AppInstallation | null;
  };
  communicationChannel: {
    isSaving: boolean;
    data: {
      id?: string;
      name: string;
      identifierValue: string;
      identifierMode?: "DEDICATED_PHONE" | "TEXT";
      definitionHandle?: string;
      // Channel handle from executable.config (skedyul.config.ts)
      handle?: string;
      settingsValues?: Record<string, unknown>;
      channelSettings?: Array<{
        handle: string;
        label: string;
        description: string;
        icon: string;
        placeholder: string;
        required: boolean;
        variableKey: string;
        parenthandle?: string;
        order: number;
        control: {
          type: "input" | "text-area" | "select" | "checkbox";
          dataType: "string" | "number" | "boolean" | "date" | "json";
          options?: Array<{ label: string; value: string }>;
        };
      }>;
      phoneNumbers?: { id: string; phone: string }[];
      linkedFields?: Array<{ id: string; label: string; suffix?: string }>;
      // UI-only staged model mappings per entity for the sidebar
      stagedModelMappings?: Array<{
        entityHandle: string;
        models: Array<{ id: string; label: string }>;
      }>;
    } | null;
  };
  // Install wizard UI state
  installWizard?: {
    stagedChannels: Array<{
      id: string;
      // Channel handle from executable.config (skedyul.config.ts)
      handle: string;
      name: string;
      identifierValue: string;
      identifierMode: "DEDICATED_PHONE" | "TEXT";
      definitionHandle: string;
      settingsValues: Record<string, unknown>;
      linkedFields?: string[];
      // UI-only: per-appField selection override (fieldId or "__create__")
      appFields?: Record<string, string>;
    }>;
    // Associations staged during install (grouped by App Entity)
    associations?: Array<{
      id: string;
      modelId: string;
      entityHandle: string;
    }>;
    // Environment variables staged during install
    environmentVariables?: Record<string, string>;
  };
  appEditor: {
    isSaving: boolean;
    data: {
      name: string;
      description: string;
      shortDescription: string;
      tagsInput: string;
      publisherName: string;
      websiteUrl: string;
      projectDirectory: string;
      computeLayer?: ComputeLayer;
      runtime?: "NODEJS_22";
      environmentVersions?: Record<string, string | null>; // environmentId -> versionId
      installEnvVariables?: Array<{
        id: string;
        key: string;
        label: string;
        description: string;
        visibility: "ENCRYPTED" | "VISIBLE";
      }>;
    } | null;
  };
  appVersions: {
    isLoading: boolean;
    appId: string | null;
    data: AppVersionWithExecutable[];
  };
  versionEnvVariables: {
    isLoading: boolean;
    versionHandle: string | null;
    data: VersionEnvVariable[];
  };
}

const initialState: appState = {
  apps: {
    isLoading: false,
    data: [],
  },
  currentApp: {
    isLoading: false,
    data: null,
  },
  // removed appCommunicationChannels local cache
  installing: false,
  appInstall: {
    isLoading: false,
    data: null,
  },
  communicationChannel: {
    isSaving: false,
    data: null,
  },
  installWizard: {
    stagedChannels: [],
    associations: [],
    environmentVariables: {},
  },
  appEditor: {
    isSaving: false,
    data: null,
  },
  appVersions: {
    isLoading: false,
    appId: null,
    data: [],
  },
  versionEnvVariables: {
    isLoading: false,
    versionHandle: null,
    data: [],
  },
};

type CommunicationChannelData = NonNullable<
  appState["communicationChannel"]["data"]
>;

type AppEditorField =
  | "name"
  | "description"
  | "shortDescription"
  | "tagsInput"
  | "publisherName"
  | "websiteUrl"
  | "projectDirectory"
  | "computeLayer"
  | "runtime";

type AppEditorConfig = {
  projectDirectory?: string;
  computeLayer?: ComputeLayer;
  runtime?: "NODEJS_22";
  environmentVersions?: Record<string, string | null>;
  env?: Array<{
    id: string;
    key: string;
    label: string;
    description: string;
    visibility: "ENCRYPTED" | "VISIBLE";
  }>;
} | null;

export type GetAppsInput = {
  includeMarketplace?: boolean;
  includeAppVersions?: boolean;
};

export const getApps = createAsyncThunk(
  "apps/getApps",
  async (input?: GetAppsInput) => {
    return await trpc.apps.getApps.query(input);
  },
);

export type GetAppThunkInput =
  | {
      handle: string;
      returnType: "developer-console" | "marketplace";
    }
  | {
      id: string;
      returnType: "developer-console" | "marketplace";
    };

export const getApp = createAsyncThunk(
  "apps/getApp",
  async (input: GetAppThunkInput) => {
    return await trpc.apps.getApp.query(input);
  },
);

// Deprecated thunk getAppCommunicationChannels removed. Use getInstallation which returns per-app definitions.

export const installApp = createAsyncThunk(
  "apps/installApp",
  async (input: InstallAppInput) => {
    const res = await trpc.apps.installApp.mutate(input);
    return res;
  },
);

export const getInstallation = createAsyncThunk(
  "apps/getInstallation",
  async (input: {
    appHandle: string;
    installId: string;
    // Optional: when provided, also returns rendered page content
    pagePath?: string;
    pathParams?: Record<string, string>;
  }) => {
    return await trpc.apps.getInstallation.query({
      appHandle: input.appHandle,
      installId: input.installId,
      pagePath: input.pagePath,
      pathParams: input.pathParams,
    });
  },
);

export const getAppVersions = createAsyncThunk<
  { appId: string; versions: AppVersionWithExecutable[] },
  { appId: string; force?: boolean },
  { state: RootState }
>(
  "apps/getAppVersions",
  async ({ appId }) => {
    const response = await trpc.apps.getAppVersions.query({ appId });
    return { appId, versions: response.versions as AppVersionWithExecutable[] };
  },
  {
    // Prevent duplicate fetches - runs synchronously before async payload
    condition: ({ appId, force }, { getState }) => {
      const { apps } = getState();
      // Always allow force refresh
      if (force) {
        return !apps.appVersions.isLoading;
      }
      // Don't fetch if already loading
      if (apps.appVersions.isLoading) {
        return false;
      }
      // Don't fetch if we already have data for this appId
      if (
        apps.appVersions.appId === appId &&
        apps.appVersions.data.length > 0
      ) {
        return false;
      }
      return true;
    },
  },
);

// Consolidated thunk for developer console - fetches app, versions, and optionally env variables
type GetDevAppWithVersionsInput = {
  appId: string;
  versionHandle?: string;
  force?: boolean;
};

type GetDevAppWithVersionsResult = {
  app: GetApp;
  versions: AppVersionWithExecutable[];
  envVariables: VersionEnvVariable[] | null;
  versionHandle: string | null;
};

export const getDevAppWithVersions = createAsyncThunk<
  GetDevAppWithVersionsResult,
  GetDevAppWithVersionsInput,
  { state: RootState }
>(
  "apps/getDevAppWithVersions",
  async ({ appId, versionHandle }) => {
    const response = await trpc.apps.getDevAppWithVersions.query({
      appId,
      versionHandle,
    });
    return {
      app: response.app as unknown as GetApp,
      versions: response.versions as AppVersionWithExecutable[],
      envVariables: response.envVariables,
      versionHandle: versionHandle ?? null,
    };
  },
  {
    // Prevent duplicate fetches
    condition: ({ appId, versionHandle, force }, { getState }) => {
      const { apps } = getState();
      // Always allow force refresh
      if (force) {
        return !apps.currentApp.isLoading && !apps.appVersions.isLoading;
      }
      // Don't fetch if already loading
      if (apps.currentApp.isLoading || apps.appVersions.isLoading) {
        return false;
      }
      // Don't fetch if we already have data for this appId
      const hasAppData = apps.currentApp.data?.id === appId;
      const hasVersionsData =
        apps.appVersions.appId === appId && apps.appVersions.data.length > 0;
      const hasEnvData =
        !versionHandle ||
        apps.versionEnvVariables.versionHandle === versionHandle;

      if (hasAppData && hasVersionsData && hasEnvData) {
        return false;
      }
      return true;
    },
  },
);

export const saveApp = createAsyncThunk<
  GetApp | null,
  void,
  { state: RootState }
>("apps/saveApp", async (_, { getState }) => {
  const state = getState() as RootState;
  const currentApp = state.apps.currentApp.data;
  const editor = state.apps.appEditor.data;

  if (!currentApp || !editor) {
    throw new Error("Cannot save app without current app and editor state");
  }

  const safeCurrentApp: GetApp = currentApp;

  const trimmedTags = editor.tagsInput
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

  const payload: UpdateAppInput = {
    appId: safeCurrentApp.id,
    name: editor.name || safeCurrentApp.name,
    description: editor.description || null,
    shortDescription: editor.shortDescription || null,
    tags: trimmedTags,
    publisherName: editor.publisherName || null,
    websiteUrl: editor.websiteUrl || null,
    publishedAt: undefined,
    config: {
      projectDirectory:
        editor.projectDirectory.trim().length > 0
          ? editor.projectDirectory.trim()
          : undefined,
      computeLayer: editor.computeLayer ?? undefined,
      runtime: editor.runtime ?? undefined,
      env:
        editor.installEnvVariables && editor.installEnvVariables.length > 0
          ? editor.installEnvVariables
          : undefined,
      environmentVersions: editor.environmentVersions,
    },
  };

  await trpc.apps.updateApp.mutate(payload);

  const nextApp: GetApp = {
    ...safeCurrentApp,
    name: payload.name ?? safeCurrentApp.name,
    description: payload.description ?? null,
    shortDescription: payload.shortDescription ?? null,
    tags: (payload.tags ?? safeCurrentApp.tags) as GetApp["tags"],
    publisherName: payload.publisherName ?? null,
    websiteUrl: payload.websiteUrl ?? null,
    config: {
      ...(safeCurrentApp.config as
        | {
            projectDirectory?: string;
            computeLayer?: ComputeLayer;
            runtime?: "NODEJS_22";
          }
        | null
        | undefined),
      ...(payload.config ?? {}),
    },
  };

  return nextApp;
});

// Stage a communication channel and compute initial appFields via quick lookup
export const setStagedChannel = createAsyncThunk(
  "apps/setStagedChannel",
  (
    input: {
      id: string;
      // Channel handle from executable.config (skedyul.config.ts)
      handle: string;
      name: string;
      identifierValue: string;
      identifierMode: "DEDICATED_PHONE" | "TEXT";
      definitionHandle: string;
      settingsValues: Record<string, unknown>;
      linkedFields?: string[];
    },
    { getState },
  ) => {
    const state = getState() as RootState;

    // Locate app/channel definition by handle (id is now handle from executable.config)
    const apps = state.apps.apps.data;
    let appDef: ListApp | undefined;
    let channelDef: AppCommunicationChannel | undefined;
    for (const a of apps) {
      const found = (a?.appCommunicationChannels || []).find(
        (c) => c.handle === input.handle || c.id === input.handle,
      );
      if (found) {
        appDef = a;
        channelDef = found;
        break;
      }
    }

    // AppCommunicationChannel doesn't have config - app fields come from app definition only
    const globalAppFields: AppFieldConfig[] = appDef?.config?.appFields ?? [];
    const effectiveAppFields: AppFieldConfig[] = [...globalAppFields];

    // Build quick lookup matches using selected associations and CRM models
    const associations = state.apps.installWizard?.associations || [];
    type CRMModel = RootState["crm"]["models"]["data"][number];
    const models: CRMModel[] = state.crm.models.data || [];
    const appFields: Record<string, string> = {};

    for (const af of effectiveAppFields) {
      const assoc = associations.find(
        (a) => a.entityHandle === af.entityHandle,
      );
      const modelId = assoc?.modelId || "";
      const model = models.find((m) => m.id === modelId);
      const defHandle = af.definition ?? "";
      const mf = (model?.fields || []).find(
        (f) => (f.definition?.handle ?? "") === defHandle,
      );
      const appFieldKey = `${af.entityHandle}:${af.fieldHandle}`;
      appFields[appFieldKey] = mf?.id || "__create__";
    }

    return { ...input, appFields };
  },
);

// Note: linkFieldToCommunicationChannel moved; keeping action for compatibility only if available
// Removing to avoid type errors since not used in this flow

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setCommunicationChannel(
      state,
      action: {
        payload: appState["communicationChannel"]["data"];
        type: string;
      },
    ) {
      state.communicationChannel.data = action.payload || null;
    },
    updateCommunicationChannelField(
      state,
      action: PayloadAction<{
        key: keyof CommunicationChannelData;
        value: unknown;
      }>,
    ) {
      if (!state.communicationChannel.data) {
        state.communicationChannel.data = {
          name: "",
          identifierValue: "",
        } as appState["communicationChannel"]["data"];
      }
      const target = state.communicationChannel
        .data as CommunicationChannelData;

      switch (action.payload.key) {
        case "id":
          target.id = String(action.payload.value);
          break;
        case "name":
          target.name = String(action.payload.value || "");
          break;
        case "identifierValue":
          target.identifierValue = String(action.payload.value || "");
          break;
        case "identifierMode":
          target.identifierMode = action.payload.value as
            | "DEDICATED_PHONE"
            | "TEXT"
            | undefined;
          break;
        case "definitionHandle":
          target.definitionHandle =
            (action.payload.value as string) || undefined;
          break;
        case "handle":
          target.handle = (action.payload.value as string) || undefined;
          break;
        case "settingsValues":
          target.settingsValues =
            (action.payload.value as Record<string, unknown>) || {};
          break;
        case "channelSettings":
          target.channelSettings =
            (action.payload
              .value as CommunicationChannelData["channelSettings"]) || [];
          break;
        case "phoneNumbers":
          target.phoneNumbers =
            (action.payload
              .value as CommunicationChannelData["phoneNumbers"]) || [];
          break;
        case "linkedFields":
          target.linkedFields =
            (action.payload
              .value as CommunicationChannelData["linkedFields"]) || [];
          break;
        case "stagedModelMappings":
          target.stagedModelMappings =
            (action.payload
              .value as CommunicationChannelData["stagedModelMappings"]) || [];
          break;
        default:
          break;
      }
    },
    resetCommunicationChannel(state) {
      state.communicationChannel = { isSaving: false, data: null };
    },
    clearAppInstall(state) {
      state.appInstall = { isLoading: false, data: null };
    },
    addStagedCommunicationChannel(
      state,
      action: {
        payload: {
          id: string;
          // Channel handle from executable.config (skedyul.config.ts)
          handle: string;
          name: string;
          identifierValue: string;
          identifierMode: "DEDICATED_PHONE" | "TEXT";
          definitionHandle: string;
          settingsValues: Record<string, unknown>;
          linkedFields?: string[];
          appFields?: Record<string, string>;
        };
        type: string;
      },
    ) {
      if (!state.installWizard) state.installWizard = { stagedChannels: [] };
      state.installWizard.stagedChannels = [
        ...(state.installWizard.stagedChannels || []),
        action.payload,
      ];
    },
    setStagedChannelAppField(
      state,
      action: PayloadAction<{
        id: string;
        appFieldKey: string; // e.g. "contact:email"
        value: string; // fieldId or "__create__"
      }>,
    ) {
      const list = state.installWizard?.stagedChannels || [];
      const idx = list.findIndex((c) => c.id === action.payload.id);
      if (idx >= 0) {
        const current = list[idx]!;
        const next = { ...current } as typeof current;
        next.appFields = {
          ...(next.appFields || {}),
          [action.payload.appFieldKey]: action.payload.value,
        };
        state.installWizard!.stagedChannels = [
          ...list.slice(0, idx),
          next,
          ...list.slice(idx + 1),
        ];
      }
    },
    updateStagedCommunicationChannelField(
      state,
      action: PayloadAction<{
        id: string;
        key:
          | "name"
          | "identifierValue"
          | "identifierMode"
          | "definitionHandle"
          | "settingsValues";
        value: string | ("DEDICATED_PHONE" | "TEXT") | Record<string, unknown>;
      }>,
    ) {
      const { id, key, value } = action.payload;
      const list = state.installWizard?.stagedChannels || [];
      const idx = list.findIndex((c) => c.id === id);
      if (idx >= 0) {
        const current = list[idx]!;
        const next = { ...current } as typeof current;
        switch (key) {
          case "name": {
            next.name = value as string;
            break;
          }
          case "identifierValue": {
            next.identifierValue = value as string;
            break;
          }
          case "definitionHandle": {
            next.definitionHandle = value as string;
            break;
          }
          case "identifierMode": {
            next.identifierMode = value as "DEDICATED_PHONE" | "TEXT";
            break;
          }
          case "settingsValues": {
            next.settingsValues = value as Record<string, unknown>;
            break;
          }
        }
        state.installWizard!.stagedChannels = [
          ...list.slice(0, idx),
          next,
          ...list.slice(idx + 1),
        ];
      }
    },
    removeStagedCommunicationChannel(
      state,
      action: PayloadAction<{ id: string }>,
    ) {
      const list = state.installWizard?.stagedChannels || [];
      state.installWizard!.stagedChannels = list.filter(
        (c) => c.id !== action.payload.id,
      );
    },
    // Association management for install wizard
    addAssociation(
      state,
      action: PayloadAction<{
        id: string;
        modelId: string;
        entityHandle: string;
      }>,
    ) {
      if (!state.installWizard)
        state.installWizard = { stagedChannels: [], associations: [] };
      const list = state.installWizard.associations || [];
      state.installWizard.associations = [...list, action.payload];
    },
    removeAssociation(state, action: PayloadAction<{ id: string }>) {
      const list = state.installWizard?.associations || [];
      state.installWizard!.associations = list.filter(
        (a) => a.id !== action.payload.id,
      );
    },
    updateAssociationModel(
      state,
      action: PayloadAction<{ id: string; modelId: string }>,
    ) {
      const list = state.installWizard?.associations || [];
      const idx = list.findIndex((a) => a.id === action.payload.id);
      if (idx >= 0) {
        const current = list[idx]!;
        const next = { ...current, modelId: action.payload.modelId };
        state.installWizard!.associations = [
          ...list.slice(0, idx),
          next,
          ...list.slice(idx + 1),
        ];
      }
    },
    setInstallWizardEnvVariable(
      state,
      action: PayloadAction<{ key: string; value: string }>,
    ) {
      if (!state.installWizard) {
        state.installWizard = {
          stagedChannels: [],
          associations: [],
          environmentVariables: {},
        };
      }
      if (!state.installWizard.environmentVariables) {
        state.installWizard.environmentVariables = {};
      }
      state.installWizard.environmentVariables[action.payload.key] =
        action.payload.value;
    },
    addLinkedFieldLocal(
      state,
      action: {
        payload: { id: string; label: string; suffix?: string };
        type: string;
      },
    ) {
      if (!state.communicationChannel.data) {
        return;
      }
      const existing = state.communicationChannel.data.linkedFields || [];
      const exists = existing.find((f) => f.id === action.payload.id);
      if (!exists) {
        state.communicationChannel.data.linkedFields = [
          ...existing,
          {
            id: action.payload.id,
            label: action.payload.label,
            suffix: action.payload.suffix,
          },
        ];
      }
    },
    updateAppEditorField(
      state,
      action: PayloadAction<{ key: AppEditorField; value: string }>,
    ) {
      if (!state.appEditor.data) {
        state.appEditor.data = {
          name: "",
          description: "",
          shortDescription: "",
          tagsInput: "",
          publisherName: "",
          websiteUrl: "",
          projectDirectory: "",
          computeLayer: undefined,
          runtime: undefined,
        };
      }

      switch (action.payload.key) {
        case "name":
          state.appEditor.data.name = action.payload.value;
          break;
        case "description":
          state.appEditor.data.description = action.payload.value;
          break;
        case "shortDescription":
          state.appEditor.data.shortDescription = action.payload.value;
          break;
        case "tagsInput":
          state.appEditor.data.tagsInput = action.payload.value;
          break;
        case "publisherName":
          state.appEditor.data.publisherName = action.payload.value;
          break;
        case "websiteUrl":
          state.appEditor.data.websiteUrl = action.payload.value;
          break;
        case "projectDirectory":
          state.appEditor.data.projectDirectory = action.payload.value;
          break;
        case "computeLayer": {
          const normalizedValue = action.payload.value.trim().toLowerCase();
          if (
            normalizedValue === "serverless" ||
            normalizedValue === "dedicated"
          ) {
            state.appEditor.data.computeLayer = normalizedValue as ComputeLayer;
          }
          break;
        }
        case "runtime":
          if (action.payload.value === "NODEJS_22") {
            state.appEditor.data.runtime = action.payload.value;
          }
          break;
      }
    },
    resetAppEditor(state) {
      state.appEditor = {
        isSaving: false,
        data: null,
      };
    },
    updateEnvironmentVersion(
      state,
      action: PayloadAction<{
        environmentId: string;
        versionId: string | null;
      }>,
    ) {
      if (!state.appEditor.data) return;
      if (!state.appEditor.data.environmentVersions) {
        state.appEditor.data.environmentVersions = {};
      }
      state.appEditor.data.environmentVersions[action.payload.environmentId] =
        action.payload.versionId;
    },
    addInstallEnvVariable(
      state,
      action: PayloadAction<{
        key: string;
        label: string;
        description: string;
        visibility: "ENCRYPTED" | "VISIBLE";
      }>,
    ) {
      if (!state.appEditor.data) return;
      if (!state.appEditor.data.installEnvVariables) {
        state.appEditor.data.installEnvVariables = [];
      }
      state.appEditor.data.installEnvVariables.push({
        id: `temp-${Date.now()}`,
        ...action.payload,
      });
    },
    updateInstallEnvVariable(
      state,
      action: PayloadAction<{
        id: string;
        key: string;
        label: string;
        description: string;
      }>,
    ) {
      if (!state.appEditor.data?.installEnvVariables) return;
      const index = state.appEditor.data.installEnvVariables.findIndex(
        (v) => v.id === action.payload.id,
      );
      const list = state.appEditor.data.installEnvVariables;
      if (index < 0 || index >= list.length) return;
      const current = list[index];
      if (!current) return;
      current.key = action.payload.key;
      current.label = action.payload.label;
      current.description = action.payload.description;
    },
    removeInstallEnvVariable(state, action: PayloadAction<{ id: string }>) {
      if (!state.appEditor.data?.installEnvVariables) return;
      state.appEditor.data.installEnvVariables =
        state.appEditor.data.installEnvVariables.filter(
          (v) => v.id !== action.payload.id,
        );
    },
    /**
     * Update a resource instance in appInstall.data.resources after linking a model.
     * This allows optimistic updates without requiring a full page reload.
     */
    updateResourceInstance(
      state,
      action: PayloadAction<{
        modelHandle: string;
        targetModelId: string;
        targetModelName: string;
        fieldMappings: Array<{
          provisionHandle: string;
          provisionLabel: string;
          workspaceLabel: string;
          workspaceFieldId: string;
        }>;
        status: "READY" | "INCOMPLETE" | "PENDING";
      }>,
    ) {
      if (!state.appInstall.data?.resources) return;

      const {
        modelHandle,
        targetModelId,
        targetModelName,
        fieldMappings,
        status,
      } = action.payload;

      // Type assertion for resources object
      const resources = state.appInstall.data.resources as Record<
        string,
        {
          linked: boolean;
          targetId: string | null;
          targetName: string | null;
          fieldMappings: Array<{
            provisionHandle?: string;
            provisionLabel: string;
            workspaceLabel: string;
            workspaceFieldId?: string;
          }>;
          status?: string;
          driftReason?: string | null;
          missingFieldHandles?: string[];
        }
      >;

      // Update the specific resource
      resources[modelHandle] = {
        ...resources[modelHandle],
        linked: true,
        targetId: targetModelId,
        targetName: targetModelName,
        fieldMappings,
        status,
        driftReason: null,
        missingFieldHandles: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getApps.pending, (state) => {
        state.apps.isLoading = true;
      })
      .addCase(getApps.fulfilled, (state, action) => {
        state.apps.isLoading = false;
        state.apps.data = action.payload;
      })
      .addCase(getApps.rejected, (state) => {
        state.apps.isLoading = false;
      })
      .addCase(getApp.pending, (state) => {
        state.apps.isLoading = true;
        state.currentApp.isLoading = true;
      })
      .addCase(getApp.fulfilled, (state, action) => {
        state.apps.isLoading = false;
        state.currentApp.isLoading = false;
        state.currentApp.data = action.payload;

        const app = action.payload;
        if (!app) {
          state.appEditor = { isSaving: false, data: null };
          return;
        }

        const existingIndex = state.apps.data.findIndex(
          (a) => a.handle === app.handle,
        );
        if (existingIndex >= 0) {
          state.apps.data[existingIndex] = app as unknown as ListApp;
        } else {
          state.apps.data.push(app as unknown as ListApp);
        }

        const config =
          ((app as { config?: AppEditorConfig }).config ?? null) || null;

        state.appEditor = {
          isSaving: false,
          data: {
            name: app.name ?? "",
            description: app.description ?? "",
            shortDescription: app.shortDescription ?? "",
            tagsInput:
              Array.isArray(app.tags) && app.tags.length > 0
                ? app.tags.join(", ")
                : "",
            publisherName: app.publisherName ?? "",
            websiteUrl: app.websiteUrl ?? "",
            projectDirectory: config?.projectDirectory ?? "",
            computeLayer: config?.computeLayer,
            runtime: config?.runtime,
            environmentVersions: config?.environmentVersions ?? {},
            installEnvVariables: config?.env ?? [],
          },
        };
      })
      .addCase(getApp.rejected, (state) => {
        state.apps.isLoading = false;
        state.currentApp.isLoading = false;
        state.currentApp.data = null;
        state.appEditor = { isSaving: false, data: null };
      })
      .addCase(installApp.pending, (state) => {
        state.installing = true;
      })
      .addCase(installApp.fulfilled, (state, action) => {
        state.installing = false;
        if (action.payload?.appInstallation) {
          state.appInstall.data = action.payload as unknown as AppInstallation;
        }
      })
      .addCase(installApp.rejected, (state) => {
        state.installing = false;
      });

    builder
      .addCase(getInstallation.pending, (state) => {
        state.appInstall.isLoading = true;
      })
      .addCase(getInstallation.fulfilled, (state, action) => {
        state.appInstall.isLoading = false;
        const newData = action.payload;
        const existingData = state.appInstall.data;

        // If the new payload doesn't have resources but existing data does,
        // preserve the existing resources. This prevents race conditions where
        // a layout fetch (without pagePath) overwrites a page fetch (with pagePath).
        if (
          existingData?.resources &&
          !newData.resources &&
          existingData.installation?.id === newData.installation?.id
        ) {
          state.appInstall.data = {
            ...newData,
            resources: existingData.resources,
          };
        } else {
          state.appInstall.data = newData;
        }
      })
      .addCase(getInstallation.rejected, (state) => {
        state.appInstall.isLoading = false;
        // Keep the install already on screen. Clearing it makes the setting
        // shell collapse and the page fetch start over.
      });

    builder
      .addCase(saveApp.pending, (state) => {
        state.appEditor.isSaving = true;
      })
      .addCase(saveApp.fulfilled, (state, action) => {
        state.appEditor.isSaving = false;
        const app = action.payload;
        if (!app) return;

        state.currentApp.data = app;

        const config =
          ((app as { config?: AppEditorConfig }).config ?? null) || null;

        state.appEditor = {
          isSaving: false,
          data: {
            name: app.name ?? "",
            description: app.description ?? "",
            shortDescription: app.shortDescription ?? "",
            tagsInput:
              Array.isArray(app.tags) && app.tags.length > 0
                ? app.tags.join(", ")
                : "",
            publisherName: app.publisherName ?? "",
            websiteUrl: app.websiteUrl ?? "",
            projectDirectory: config?.projectDirectory ?? "",
            computeLayer: config?.computeLayer,
            runtime: config?.runtime,
            environmentVersions: config?.environmentVersions ?? {},
            installEnvVariables: config?.env ?? [],
          },
        };
      })
      .addCase(saveApp.rejected, (state) => {
        state.appEditor.isSaving = false;
      });

    builder
      .addCase(getAppVersions.pending, (state) => {
        state.appVersions.isLoading = true;
      })
      .addCase(getAppVersions.fulfilled, (state, action) => {
        state.appVersions.isLoading = false;
        state.appVersions.appId = action.payload.appId;
        state.appVersions.data = action.payload.versions;
      })
      .addCase(getAppVersions.rejected, (state) => {
        state.appVersions.isLoading = false;
      });

    // Consolidated thunk for developer console
    builder
      .addCase(getDevAppWithVersions.pending, (state) => {
        state.currentApp.isLoading = true;
        state.appVersions.isLoading = true;
        state.versionEnvVariables.isLoading = true;
      })
      .addCase(getDevAppWithVersions.fulfilled, (state, action) => {
        state.currentApp.isLoading = false;
        state.appVersions.isLoading = false;
        state.versionEnvVariables.isLoading = false;

        // Update currentApp
        state.currentApp.data = action.payload.app;

        // Update appVersions
        state.appVersions.appId = action.payload.app.id;
        state.appVersions.data = action.payload.versions;

        // Update versionEnvVariables if provided
        if (action.payload.envVariables) {
          state.versionEnvVariables.versionHandle =
            action.payload.versionHandle;
          state.versionEnvVariables.data = action.payload.envVariables;
        }

        // Also update appEditor like getApp does
        const app = action.payload.app;
        const config =
          ((app as { config?: AppEditorConfig }).config ?? null) || null;

        state.appEditor = {
          isSaving: false,
          data: {
            name: app.name ?? "",
            description: app.description ?? "",
            shortDescription: app.shortDescription ?? "",
            tagsInput:
              Array.isArray(app.tags) && app.tags.length > 0
                ? app.tags.join(", ")
                : "",
            publisherName: app.publisherName ?? "",
            websiteUrl: app.websiteUrl ?? "",
            projectDirectory: config?.projectDirectory ?? "",
            computeLayer: config?.computeLayer,
            runtime: config?.runtime,
            environmentVersions: config?.environmentVersions ?? {},
            installEnvVariables: config?.env ?? [],
          },
        };
      })
      .addCase(getDevAppWithVersions.rejected, (state) => {
        state.currentApp.isLoading = false;
        state.appVersions.isLoading = false;
        state.versionEnvVariables.isLoading = false;
      });
    // removed linkFieldToCommunicationChannel extra reducer (not used in this flow)
  },
});

// Action creators are generated for each case reducer function
export const {
  setCommunicationChannel,
  updateCommunicationChannelField,
  resetCommunicationChannel,
  addLinkedFieldLocal,
  addStagedCommunicationChannel,
  updateStagedCommunicationChannelField,
  removeStagedCommunicationChannel,
  setStagedChannelAppField,
  addAssociation,
  removeAssociation,
  updateAssociationModel,
  setInstallWizardEnvVariable,
  updateAppEditorField,
  resetAppEditor,
  updateEnvironmentVersion,
  addInstallEnvVariable,
  updateInstallEnvVariable,
  removeInstallEnvVariable,
  clearAppInstall,
  updateResourceInstance,
} = appSlice.actions;

export default appSlice.reducer;
